import React, { ReactNode, createContext, useCallback, useContext, useState } from "react";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import useLocalStorage from "@hooks/useLocalStorage";
import { LocalStorageKeys } from "@service/LocalStorageService";
import { EndBreathingExerciseEvent, StartBreathingExerciseEvent } from "../../../AmplitudeWidgetEvents";
import BreathingWidget from "./BreathingWidget";

type BreathingWidgetContext = {
    open: () => void;
    hasSeenBreathingWidget: boolean;
};

const BreathingWidgetContext = createContext<BreathingWidgetContext | undefined>(undefined);

interface BreathingWidgetProviderProps {
    children?: ReactNode;
}

export const BreathingWidgetProvider = ({ children }: BreathingWidgetProviderProps) => {
    const amplitudeService = AmplitudeService.getInstance();
    const [isOpen, setOpen] = useState(false);
    const [hasSeenBreathingWidget, setHasSeenBreathingWidget] = useLocalStorage(
        LocalStorageKeys.HasSeenBreathingWidget,
        false,
    );

    const open = useCallback(() => {
        setHasSeenBreathingWidget(true);
        amplitudeService.logEvent(new StartBreathingExerciseEvent());
        setOpen(true);
    }, [setOpen]);

    const close = useCallback(() => {
        amplitudeService.logEvent(new EndBreathingExerciseEvent());
        setOpen(false);
    }, [setOpen]);

    return (
        <BreathingWidgetContext.Provider value={{ open, hasSeenBreathingWidget }}>
            {children}
            {isOpen && <BreathingWidget onClose={close} />}
        </BreathingWidgetContext.Provider>
    );
};

export const useBreathingWidgetContext = () => {
    const context = useContext(BreathingWidgetContext);
    if (context == null) {
        throw new Error("Missing BreathingWidgetContext.");
    }
    return context;
};
